import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import { formatDateTime, formatScore } from "../../lib/format"
import { AppShell } from "../ui/AppShell"
import { Button } from "../ui/Button"
import { Card } from "../ui/Card"
import { PageLoading } from "../ui/Spinner"
import type { AttemptsResponse, AttemptSummary } from "../../types"

function StatusBadge({ status }: { status: AttemptSummary["status"] }) {
  if (status === "in_progress")
    return (
      <span className="rounded-full bg-gold-500/15 px-2 py-0.5 text-xs font-semibold text-gold-600">
        Em andamento
      </span>
    )
  if (status === "expired")
    return (
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-navy-500">Expirado</span>
    )
  return (
    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-400">
      Finalizado
    </span>
  )
}

export default function Historico() {
  const [attempts, setAttempts] = useState<AttemptSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<AttemptsResponse>("/api/attempts")
      .then((data) => setAttempts(data.attempts))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Erro ao carregar o histórico"))
  }, [])

  if (error) {
    return (
      <AppShell>
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
          <p className="text-navy-700">{error}</p>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Tentar de novo
          </Button>
        </div>
      </AppShell>
    )
  }

  if (attempts === null) {
    return (
      <AppShell>
        <PageLoading />
      </AppShell>
    )
  }

  return (
    <AppShell>
      <h1 className="text-2xl font-bold text-navy-900">Histórico</h1>
      <p className="mt-1 text-sm text-navy-500">Todos os seus simulados</p>

      {attempts.length === 0 ? (
        <Card className="mt-5 py-12 text-center">
          <p className="text-sm text-navy-700">Nenhum simulado por aqui ainda.</p>
          <p className="mt-1 text-xs text-navy-400">Seu primeiro treino é o começo da farda.</p>
          <Link to="/dashboard" className="mt-4 inline-block">
            <Button>Fazer meu primeiro simulado</Button>
          </Link>
        </Card>
      ) : (
        <div className="mt-5 flex flex-col gap-2">
          {attempts.map((a) => (
            <Card key={a.id} className="p-4!">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-navy-900 tabular-nums">{formatDateTime(a.started_at)}</p>
                    <StatusBadge status={a.status} />
                  </div>
                  <p className="mt-1 text-xs text-navy-400">
                    {a.status === "in_progress" ? (
                      "Simulado em aberto"
                    ) : (
                      <>
                        Nota{" "}
                        <span className="font-semibold text-gold-600 tabular-nums">
                          {a.score !== null ? formatScore(a.score) : "—"}
                        </span>{" "}
                        · {a.correct_count}/{a.total_questions} acertos
                      </>
                    )}
                  </p>
                </div>
                {a.status === "in_progress" ? (
                  <Link
                    to={`/simulado/${a.id}`}
                    className="shrink-0 text-sm font-medium text-gold-600 hover:text-gold-600"
                  >
                    continuar
                  </Link>
                ) : (
                  <Link
                    to={`/simulado/${a.id}/resultado`}
                    className="shrink-0 text-sm font-medium text-gold-600 hover:text-gold-600"
                  >
                    ver resultado
                  </Link>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  )
}
